import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalContent } from '../ui/Modal';
import { Button, Switch } from '../ui';
import { apiService } from '../../services/api';
import { Loader2, Shield, Layout } from 'lucide-react';

interface Permission {
  key: string;
  label: string;
  description: string;
}

interface RolePermissions {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
  canManageProperties: boolean;
  canManageTenants: boolean;
  canManagePayments: boolean;
  canAccessDashboard?: boolean;
  canAccessProperties?: boolean;
  canAccessTenants?: boolean;
  canAccessPayments?: boolean;
  canAccessReports?: boolean;
  canAccessSettings?: boolean;
  canAccessAnalytics?: boolean;
}

interface EditRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roleName: string;
  currentPermissions: RolePermissions | Record<string, boolean>;
}

const defaultRolePermissions: RolePermissions = {
  canCreate: false,
  canRead: false,
  canUpdate: false,
  canDelete: false,
  canManageUsers: false,
  canManageProperties: false,
  canManageTenants: false,
  canManagePayments: false,
};

function toRolePermissions(input: RolePermissions | Record<string, boolean>): RolePermissions {
  return { ...defaultRolePermissions, ...input } as RolePermissions;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-height: calc(80vh - 100px);
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.muted};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.mutedForeground};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: 700px;
    margin: 0 auto;
  }
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PermissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  
  @media (min-width: 768px) {
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
  }
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${props => props.theme.muted}20;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 100%;
  
  @media (min-width: 768px) {
    padding: 20px 28px;
  }
  
  &:hover {
    background: ${props => props.theme.muted}30;
    border-color: ${props => props.theme.primary}40;
  }
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin-top: 24px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PermissionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const PermissionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
`;

const PermissionDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.destructive || '#ef4444'};
  font-size: 14px;
  margin-top: 4px;
  padding: 12px;
  background: ${props => props.theme.destructive}10;
  border: 1px solid ${props => props.theme.destructive}20;
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.success || '#10b981'};
  font-size: 14px;
  margin-top: 4px;
  padding: 12px;
  background: ${props => props.theme.success}10;
  border: 1px solid ${props => props.theme.success}20;
  border-radius: 8px;
`;

const PERMISSIONS: Permission[] = [
  {
    key: 'canCreate',
    label: 'Create',
    description: 'Create new resources (properties, tenants, payments, announcements)'
  },
  {
    key: 'canRead',
    label: 'Read',
    description: 'View and read all resources (properties, tenants, payments, reports)'
  },
  {
    key: 'canUpdate',
    label: 'Update',
    description: 'Modify existing resources (edit properties, update tenant info, modify payments)'
  },
  {
    key: 'canDelete',
    label: 'Delete',
    description: 'Delete resources (remove properties, tenants, payment records, announcements)'
  },
  {
    key: 'canManageUsers',
    label: 'Manage Users',
    description: 'Invite team members, edit roles/permissions, manage custom roles (Teams section)'
  },
  {
    key: 'canManageProperties',
    label: 'Manage Properties',
    description: 'Create/edit/delete properties, manage units, set property-wide lease templates'
  },
  {
    key: 'canManageTenants',
    label: 'Manage Tenants',
    description: 'Create/edit tenants, manage leases, send documents for signature, handle deposits'
  },
  {
    key: 'canManagePayments',
    label: 'Manage Payments',
    description: 'Record payments, view payment history, manage automations, access payment analytics'
  }
];

const PAGE_PERMISSIONS: Permission[] = [
  {
    key: 'canAccessDashboard',
    label: 'Dashboard',
    description: 'Access main dashboard with statistics, property overview, and recent activity'
  },
  {
    key: 'canAccessProperties',
    label: 'Properties',
    description: 'Access properties management page (view, edit, manage units) - Navigation link visibility'
  },
  {
    key: 'canAccessTenants',
    label: 'Tenants',
    description: 'Access tenants management page (view, edit tenant records) - Navigation link visibility'
  },
  {
    key: 'canAccessPayments',
    label: 'Payments',
    description: 'Access payments management page (view payment history, record payments) - Navigation link visibility'
  },
  {
    key: 'canAccessReports',
    label: 'Reports',
    description: 'Access reports page (financial reports, tenant reports, data export) - Coming soon'
  },
  {
    key: 'canAccessSettings',
    label: 'Settings',
    description: 'Access system settings modal (billing, preferences, notifications, configuration)'
  },
  {
    key: 'canAccessAnalytics',
    label: 'Analytics',
    description: 'Access analytics and insights page (advanced analytics, performance metrics) - Coming soon'
  }
];

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  roleName,
  currentPermissions
}) => {
  const [permissions, setPermissions] = useState<RolePermissions>(() => toRolePermissions(currentPermissions));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPermissions(toRolePermissions(currentPermissions));
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, currentPermissions]);

  const handlePermissionChange = (key: keyof RolePermissions, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const payload: Record<string, boolean> = {};
      (Object.keys(permissions) as Array<keyof RolePermissions>).forEach((key) => {
        const v = permissions[key];
        if (typeof v === 'boolean') payload[key] = v;
      });
      await apiService.updateRolePermissions(roleName, payload);
      setSuccess('Role permissions updated successfully');
      setTimeout(() => {
        onSuccess();
        onClose();
        setSuccess(null);
      }, 1500);
    } catch (err: any) {
      console.error('Error updating role permissions:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update role permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPermissions(toRolePermissions(currentPermissions));
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit ${roleName} Permissions`} size="md" zIndex={2000}>
      <ModalContent>
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                <Shield size={18} />
                Resource Permissions
              </Label>
              <PermissionsList>
                {PERMISSIONS.map((permission) => (
                  <PermissionItem key={permission.key}>
                    <PermissionInfo>
                      <PermissionLabel>{permission.label}</PermissionLabel>
                      <PermissionDescription>{permission.description}</PermissionDescription>
                    </PermissionInfo>
                    <Switch
                      checked={permissions[permission.key as keyof RolePermissions] || false}
                      onCheckedChange={(checked) => 
                        handlePermissionChange(permission.key as keyof RolePermissions, checked)
                      }
                      disabled={loading}
                    />
                  </PermissionItem>
                ))}
              </PermissionsList>
            </FormGroup>

            <FormGroup>
              <SectionTitle>
                <Layout size={18} />
                System Pages Access
              </SectionTitle>
              <PermissionsList>
                {PAGE_PERMISSIONS.map((permission) => (
                  <PermissionItem key={permission.key}>
                    <PermissionInfo>
                      <PermissionLabel>{permission.label}</PermissionLabel>
                      <PermissionDescription>{permission.description}</PermissionDescription>
                    </PermissionInfo>
                    <Switch
                      checked={permissions[permission.key as keyof RolePermissions] ?? false}
                      onCheckedChange={(checked) => 
                        handlePermissionChange(permission.key as keyof RolePermissions, checked)
                      }
                      disabled={loading}
                    />
                  </PermissionItem>
                ))}
              </PermissionsList>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <ButtonGroup>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                    Saving...
                  </>
                ) : (
                  'Save Permissions'
                )}
              </Button>
            </ButtonGroup>
          </form>
        </FormContainer>
      </ModalContent>
    </Modal>
  );
};

